import React, { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import {
  FormField,
  FieldType,
  getFieldWidth,
  isFullWidthField,
} from './utils/formFieldTypes';
import Successpopup from '../../SharedComponent/Successpopup';
import ConsentPopup from './components/ConsentPopup';
import PhoneOTPComponent from './components/PhoneOTPComponent';
import PhoneInputComponent from './components/PhoneInputComponent';
import ConsentSelectComponent from './components/ConsentSelectComponent';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import { renderError, renderFieldLabel } from './utils';
import Axios from '../../../../config/axios';
import AuthenticatedImage from '../../SharedComponent/AuthenticatedImage';
import moment from 'moment';
import { BorderColor } from '@mui/icons-material';
import zIndex from '@mui/material/styles/zIndex';
import { Ba } from 'react-flags-select';

interface FormDynamicElementProps {
  elementDetail: FormField;
  onChange: (field: FormField) => void;
  currentStepData: FormField[];
  selectedValues: Record<number, any>;
  setSelectedValues: (updater: (prev: any) => any) => void;
  validationErrors: Record<number, string>;
  onAddressSelected?: (placeResult: google.maps.places.PlaceResult, systemIdentifier: string) => void;
}

export interface SumsubImage {
  id: string;
  imageId: string;
  imageUrl: string | null;
  fileName: string;
  fileType: string;
  fileSize: number;
  resolution?: {
    width: number;
    height: number;
  };
  docType: string;
  country?: string;
  verified: boolean;
  reviewAnswer: 'GREEN' | 'RED' | 'PENDING';
  addedDate: string;
  deactivated: boolean;
  error?: string;
}

export interface SumsubUploadedImagesResponse {
  applicantId: string;
  images: SumsubImage[];
  totalImages: number;
  reviewStatus: string;
  statistics: {
    total: number;
    verified: number;
    pending: number;
    rejected: number;
  };
}

const FormDynamicElement: React.FC<FormDynamicElementProps> = ({
  elementDetail,
  onChange,
  currentStepData,
  selectedValues,
  setSelectedValues,
  validationErrors,
  onAddressSelected,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({ heading: '', message: '', success: false });
  const [showConsentPopup, setShowConsentPopup] = useState(false);
  const [consentField, setConsentField] = useState<FormField | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filePreviews, setFilePreviews] = useState<Record<number, string>>({});
  const fileInputRefs = useRef<Record<number, HTMLInputElement>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  // Open external links in new tab
  useEffect(() => {
    if (containerRef.current) {
      const links = containerRef.current.querySelectorAll('a');
      links.forEach((link) => {
        link.setAttribute('target', '_blank');
      });
    }
  }, [elementDetail]);

  const errorMessage = validationErrors[elementDetail.id];

  // Handle common input change
  const handleInputChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    const fieldId = Number(name);
    const newValue = type === 'checkbox' ? checked : value;

    setSelectedValues((prev) => ({
      ...prev,
      [fieldId]: newValue,
    }));

    onChange({ ...elementDetail, value: newValue });
  };

  // Handle select/dropdown change with consent logic
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    const fieldId = Number(name);

    setSelectedValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));

    // Check if this is a consent_select field
    if (elementDetail.type === FieldType.CONSENT_SELECT
      //  && elementDetail.consent
    ) {
      setConsentField(elementDetail);
      setShowConsentPopup(true);
    }

    onChange({ ...elementDetail, value });
  };

  // Handle consent acceptance
  const handleConsentAccept = () => {
    setShowConsentPopup(false);
    setConsentField(null);
    // Value is already set in selectedValues
  };

  const handleConsentDecline = () => {
    setShowConsentPopup(false);
    // Reset the value
    setSelectedValues((prev) => ({
      ...prev,
      [elementDetail.id]: '',
    }));
    onChange({ ...elementDetail, value: '' });
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const acceptedTypes = elementDetail.accept || 'image/jpeg,image/png,application/pdf';
    const acceptedTypesArray = acceptedTypes.split(',').map((t) => t.trim());

    // Validate file type
    const isFileTypeValid = acceptedTypesArray.some((acceptedType) => {
      if (acceptedType.includes('*')) {
        const category = acceptedType.split('/')[0];
        return file.type.startsWith(`${category}/`);
      }
      return file.type === acceptedType;
    });

    if (!isFileTypeValid) {
      showErrorPopup(
        'Invalid File Type',
        `Please select a supported file format: ${acceptedTypesArray.map((t) => {
          if (t === 'application/pdf') return 'PDF';
          if (t === 'image/jpeg') return 'JPG';
          if (t === 'image/png') return 'PNG';
          return t;
        }).join(', ')}`
      );

      if (fileInputRefs.current[elementDetail.id]) {
        fileInputRefs.current[elementDetail.id].value = '';
      }
      return;
    }

    // Validate file size
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showErrorPopup('File Too Large', 'File size should be less than 10MB');
      if (fileInputRefs.current[elementDetail.id]) {
        fileInputRefs.current[elementDetail.id].value = '';
      }
      return;
    }

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setFilePreviews((prev) => {
      if (prev[elementDetail.id]) {
        URL.revokeObjectURL(prev[elementDetail.id]);
      }
      return { ...prev, [elementDetail.id]: previewUrl };
    });

    setSelectedValues((prev) => ({
      ...prev,
      [elementDetail.id]: files,
    }));

    onChange({ ...elementDetail, value: files });
  };

  const handleRemoveFile = () => {
    if (filePreviews[elementDetail.id]) {
      URL.revokeObjectURL(filePreviews[elementDetail.id]);
    }

    setFilePreviews((prev) => ({
      ...prev,
      [elementDetail.id]: null,
    }));

    setSelectedValues((prev) => ({
      ...prev,
      [elementDetail.id]: null,
    }));

    onChange({ ...elementDetail, value: null });

    if (fileInputRefs.current[elementDetail.id]) {
      fileInputRefs.current[elementDetail.id].value = '';
    }
  };

  const showErrorPopup = (heading: string, message: string) => {
    setPopupData({ heading, message, success: false });
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData({ heading: '', message: '', success: false });
  };

  // Different renderers for different field types
  // Render phone input field
  const renderPhoneField = () => {
    const handlePhoneChange = (value: string, data: any) => {
      setSelectedValues((prev) => ({
        ...prev,
        [elementDetail.id]: value,
      }));

      // Format phone value with country code if available
      // const formattedValue = `${data && 'countryCode' in data && typeof data.countryCode === 'string'
      //   ? data.countryCode.toUpperCase()
      //   : '' 
      //   }-${value}`;

      const formattedValue = `+${value}`;

      onChange({
        ...elementDetail,
        value: formattedValue,
      });
    };

    return (
      <PhoneInputComponent
        field={elementDetail}
        value={elementDetail.value || ''}
        onChange={handlePhoneChange}
        disabled={elementDetail.disabled}
        errorMessage={errorMessage}
        containerStyle={{ width: getFieldWidth(elementDetail.type) }}
      />
    );
  };

  const renderTextField = () => {
    const inputType = elementDetail.type === FieldType.PASSWORD ? (showPassword ? 'text' : 'password') : 'text';

    return (
      <FormControl style={{ width: getFieldWidth(elementDetail.type) }} margin='normal' fullWidth={isFullWidthField(elementDetail.type)}>
        {renderFieldLabel(elementDetail)}
        <TextField
          name={elementDetail.id.toString()}
          value={elementDetail.value || ''}
          variant='outlined'
          onChange={handleInputChange}
          disabled={elementDetail.disabled}
          type={inputType}
          autoComplete='new-password'
          InputProps={{
            endAdornment:
              elementDetail.type === FieldType.PASSWORD ? (
                <InputAdornment position='end'>
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge='end' size='small'>
                    {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ) : null,
            style: {
              color: elementDetail.disabled ? '#fff' : undefined,
              WebkitTextFillColor: elementDetail.disabled ? '#fff' : undefined,
            },
          }}
          sx={{
            width: getFieldWidth(elementDetail.type),
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: errorMessage ? '#f44336' : '#a3a3a3',
              },
              '&.Mui-focused fieldset': {
                borderColor: errorMessage ? '#f44336' : '#f2674a',
              },
              "& .MuiInputBase-input.Mui-disabled": {
                cursor: "not-allowed",
                pointerEvents: "auto",
                WebkitTextFillColor: "white",
                color: "white",
                opacity: 1,
              },
            },
          }}
        />
        {renderError(errorMessage)}
      </FormControl>
    );
  };

  const renderTextarea = () => {
    return (
      <FormControl style={{ width: '100%' }} margin='normal' fullWidth>
        {renderFieldLabel(elementDetail)}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            border: `1.5px solid ${errorMessage ? '#f44336' : '#FFFFFF33'}`,
            borderRadius: '12px',
            backgroundColor: '#FFFFFF0A',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
            '&:hover': {
              borderColor: elementDetail.disabled ? '#FFFFFF33' : '#FFFFFF66',
            },
            '&:focus-within': {
              borderColor: errorMessage ? '#f44336' : '#f2674a',
              borderWidth: '2px',
            },
          }}
        >
          <Box
            component='textarea'
            name={elementDetail.id.toString()}
            value={elementDetail.value || ''}
            onChange={handleInputChange}
            disabled={elementDetail.disabled}
            placeholder={elementDetail.placeholder || 'Enter your text here...'}

            sx={{
              width: '100%',
              minHeight: '120px',
              maxHeight: '250px',
              padding: '14px 16px',
              margin: 0,
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              color: '#FFFFFF',
              fontSize: '14px',
              lineHeight: '1.6',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              resize: 'vertical',
              overflow: 'auto',
              boxSizing: 'border-box',
              display: 'block',
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: 'transparent', borderRadius: '10px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                border: '2px solid transparent',
                backgroundClip: 'padding-box',
              },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(255, 255, 255, 0.35)' },
              '&::placeholder': {
                color: '#FFFFFF66',
                opacity: 0.7,
              },
              '&:disabled': {
                opacity: 0.6,
                cursor: 'not-allowed',
              },
            }}
          />
        </Box>

        {renderError(errorMessage)}
      </FormControl>
    );
  };

  const renderSelectField = () => {
    const options = elementDetail.options || [];
    return (
      <div style={{ width: getFieldWidth(elementDetail.type) }}>
        <FormControl fullWidth margin='normal'>
          {renderFieldLabel(elementDetail)}
          {/* <Select
            name={elementDetail.id.toString()}
            value={elementDetail.value || ''}
            onChange={handleSelectChange}
            disabled={elementDetail.disabled}
            variant='outlined'
            sx={{
              p: '13px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: errorMessage ? '#f44336 !important' : '#a3a3a3 !important',
                },
                '&.Mui-focused fieldset': {
                  borderColor: errorMessage ? '#f44336 !important' : '#f2674a !important',
                },
              },

              '& .MuiSelect-select.Mui-disabled': {
                cursor: 'not-allowed',
                pointerEvents: 'auto',
                WebkitTextFillColor: 'white',
                color: 'white',
                opacity: 1,
              },

              '& .MuiSvgIcon-root.Mui-disabled': {
                color: 'white',
                opacity: 1,
              },
            }}
          >
            <MenuItem value=''>-- Select --</MenuItem>
            {elementDetail.options?.map((option, index) => (
              <MenuItem key={option.id || index} value={option.answer}>
                {option.title}
              </MenuItem>
            ))}
          </Select> */}
          <Autocomplete
            options={options}
            getOptionLabel={(option) => option.title || ''}
            value={
              options.find((opt) => opt.answer === elementDetail.value || null)
            }
            onChange={(event, newValue) => {
              const value = newValue ? newValue.answer : '';

              setSelectedValues((prev) => ({
                ...prev,
                [elementDetail.id]: value,
              }));
              onChange({ ...elementDetail, value });
            }}
            disabled={elementDetail.disabled}
            renderInput={(params) => (
              <TextField
                {...params}
              />
            )}
            PaperComponent={({ children }) => (
              <div
                style={{
                  background: '#636363',
                  color: "#ffffff",
                }}
              >
                {children}
              </div>
            )}
            sx={{
              ...(elementDetail.disabled && {
                cursor: 'not-allowed',
                '& *': {
                  cursor: 'not-allowed !important',
                },
              }),

              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#fff !important',
                color: '#fff !important',
                opacity: 1,
              },
            }}
          />
        </FormControl>
        {renderError(errorMessage)}
      </div>
    );
  };

  const renderConsentSelectField = () => {
    return (
      <ConsentSelectComponent
        field={elementDetail}
        errorMessage={errorMessage}
      />
    );
  };

  const renderRadioField = () => {
    return (
      <div style={{ width: getFieldWidth(elementDetail.type) }}>
        <FormControl fullWidth margin='normal'>
          {renderFieldLabel(elementDetail)}
          <RadioGroup
            name={elementDetail.id.toString()}
            value={elementDetail.value === true ? 'true' : elementDetail.value === false ? 'false' : elementDetail.value || ''}
            onChange={handleInputChange}
          >
            {elementDetail.options?.map((option, index) => (
              <FormControlLabel
                key={option.id || index}
                value={String(option.answer)}
                control={<Radio />}
                label={option.title}
                className='text-secondary'
              />
            ))}
          </RadioGroup>
          {renderError(errorMessage)}
        </FormControl>
      </div>
    );
  };

  const renderCheckboxField = () => {
    return (
      <Box sx={{ width: '100%', marginTop: '16px', marginBottom: '16px' }}>
        {/* Field Label */}
        {renderFieldLabel(elementDetail)}

        {/* Main Checkbox Card */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            border: errorMessage ? '2px solid #f44336' : '1px solid #a3a3a3',
            borderRadius: '6px',
          }}
        >
          {/* Checkbox */}
          <Checkbox
            name={elementDetail.id.toString()}
            checked={elementDetail.value || false}
            onChange={handleInputChange}
            sx={{
              marginTop: '2px',
              color: '#f2674a',
              '&.Mui-checked': {
                color: '#f2674a',
              },
              flexShrink: 0,
            }}
          />

          {/* Text Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Main Text / Button Text */}
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#f3efef',
                marginBottom: elementDetail.options && elementDetail.options.length > 0 ? '8px' : '0px',
                lineHeight: 1.5,
              }}
            >
              {elementDetail.button_text}
              {!elementDetail.isOptional && (
                <sup style={{ marginLeft: '4px', color: '#fa0000' }}>
                  <i className='fa-solid fa-star-of-life' style={{ fontSize: '0.5rem' }}></i>
                </sup>
              )}
            </Typography>

            {/* Multiple Options if available */}
            {elementDetail.options && elementDetail.options.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginBottom: elementDetail.hint ? '8px' : '0px',
                }}
              >
                {elementDetail.options.map((option, index) => (
                  <Typography
                    key={option.id || index}
                    sx={{
                      fontSize: '12px',
                      color: '#f3efef',
                      lineHeight: 1.6,
                      '& a': {
                        color: '#f2674a',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      },
                    }}
                    className='custom-html-section'
                    dangerouslySetInnerHTML={{ __html: option.title }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
        {/* Error Message */}
        {renderError(errorMessage)}
      </Box>
    );
  };

  const renderDateField = () => {
    const minDate = moment().subtract(18, 'years').toDate();

    // Parse stored YYYY-MM-DD string safely with moment (local time, no UTC shift)
    let selectedDate: Date | null = null;
    if (elementDetail.value) {
      const m = moment(elementDetail.value, 'YYYY-MM-DD', true); // strict mode
      if (m.isValid()) {
        selectedDate = m.toDate(); // safe: parsed in LOCAL time, not UTC
      }
    }

    return (
      <div style={{ width: getFieldWidth(elementDetail.type) }}>
        <FormControl fullWidth margin="normal">
          {renderFieldLabel(elementDetail)}
          <DatePicker
            showIcon
            showMonthDropdown
            showYearDropdown
            withPortal
            dropdownMode="select"
            selected={selectedDate}
            dateFormat="MM/dd/yyyy"
            maxDate={
              elementDetail.system_identifire === 'dateOfBirth' ? minDate : undefined
            }
            onChange={(date: Date | null) => {
              handleInputChange({
                target: {
                  name: elementDetail.id.toString(),
                  // Store ONLY the date string — moment formats without time/timezone
                  value: date ? moment(date).format('YYYY-MM-DD') : null,
                },
              });
            }}
          />
          {renderError(errorMessage)}
        </FormControl>
      </div>
    );
  };

  const renderFileUploadField = () => {
    const filePreview = filePreviews[elementDetail.id];
    const hasFile = elementDetail.value instanceof FileList && elementDetail.value.length > 0;
    const savedImage = elementDetail.value?.previewUrl ? elementDetail.value : null;

    const isVerified = savedImage?.isVerified || false;
    const verificationStatus = savedImage?.verificationStatus || 'PENDING';
    const rejectReason = savedImage?.rejectReason || null;
    const rejectComment = savedImage?.rejectComment || null;

    const getBorderColor = () => {
      if (verificationStatus === 'GREEN') return '#4EDF6D'; // Green for verified
      if (verificationStatus === 'RED') return '#FF4444'; // Red for rejected
      return '#FFA500'; // Orange for pending
    };

    const getStatusBadge = () => {
      if (verificationStatus === 'GREEN') {
        return {
          text: '✓ Verified',
          color: '#4EDF6D',
          bgColor: '#4EDF6D33',
        };
      }
      if (verificationStatus === 'RED') {
        return {
          text: '✗ Rejected',
          color: '#FF4444',
          bgColor: '#FF444433',
        };
      }
      return {
        text: '⏳ Pending Verification',
        color: '#FFA500',
        bgColor: '#FFA50033',
      };
    };


    const handleOpenImageInNewTab = async (imageUrl: string) => {
      try {
        const response = await Axios.get(imageUrl, {
          responseType: 'blob',
        });

        const blobUrl = URL.createObjectURL(response.data);

        const newWindow = window.open(blobUrl, '_blank');

        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000); // 1 minute

        if (!newWindow) {
          showErrorPopup(
            'Popup Blocked',
            'Please allow popups to view the image in a new tab'
          );
        }
      } catch (error) {
        console.error('Failed to open image:', error);
        showErrorPopup('Error', 'Failed to open image. Please try again.');
      }
    };

    const statusBadge = getStatusBadge();

    return (
      <FormControl style={{ width: '100%', margin: '16px 0', flex: '0 0 100%' }} margin='normal' fullWidth>
        {renderFieldLabel(elementDetail)}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            width: '100%',
          }}
        >
          {/* Saved Image with Verification Status */}
          {savedImage && !filePreview && (
            <Box
              sx={{
                width: '100%',
                padding: '16px',
                backgroundColor: '#FFFFFF0A',
                borderRadius: '12px',
                border: `2px solid ${getBorderColor()}`,
                transition: 'all 0.3s ease',
              }}
            >
              {/* Header with Status */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                {/* Status Badge */}
                <Box
                  sx={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: statusBadge.bgColor,
                    color: statusBadge.color,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {statusBadge.text}
                </Box>

                {/* Remove Button */}
                <Box
                  onClick={() => {
                    setSelectedValues((prev) => ({
                      ...prev,
                      [elementDetail.id]: null,
                    }));
                    onChange({ ...elementDetail, value: null });
                  }}
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    backgroundColor: '#FF444433',
                    color: '#FF4444',
                    fontSize: '11px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: '#FF4444',
                      color: '#FFF',
                    },
                  }}
                >
                  Remove
                </Box>
              </Box>

              {/* Reject Reason Alert (if rejected) */}
              {verificationStatus === 'RED' && rejectReason && (
                <Box
                  sx={{
                    padding: '12px',
                    marginBottom: '12px',
                    backgroundColor: '#FF444411',
                    border: '1px solid #FF444433',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'start',
                    gap: '8px',
                  }}
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                    fill='none'
                    stroke='#FF4444'
                    strokeWidth='2'
                    style={{ flexShrink: 0, marginTop: '2px' }}
                  >
                    <path d='M10 18a8 8 0 100-16 8 8 0 000 16zM10 6v4M10 14h.01' />
                  </svg>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '12px', color: '#FF4444', fontWeight: 600, marginBottom: '4px' }}>
                      Document Rejected
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#FFFFFFAA' }}>
                      Reason: {rejectReason}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: '#FFFFFF77', marginTop: '4px' }}>
                      {rejectComment?.trim() || 'Please upload a new document that meets the requirements.'}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Image Preview */}
              {savedImage?.previewUrl && (
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#FFFFFF0A',
                    border: `1px solid ${getBorderColor()}33`,
                    cursor: 'pointer',
                    maxHeight: '300px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: getBorderColor(),
                      transform: 'scale(1.01)',
                    },
                  }}
                  onClick={() => {
                    handleOpenImageInNewTab(savedImage.previewUrl);
                  }}
                >
                  <AuthenticatedImage
                    src={savedImage.previewUrl}
                    alt={savedImage.fileName || 'Uploaded document'}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                    }}
                    fallbackContent={
                      <Box
                        sx={{
                          padding: '40px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FFFFFF66',
                          fontSize: '14px',
                        }}
                      >
                        <svg
                          width='48'
                          height='48'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                        >
                          <path d='M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z'></path>
                          <polyline points='13 2 13 9 20 9'></polyline>
                        </svg>
                        <span style={{ marginTop: '12px' }}>
                          {savedImage.fileName || 'Document'}
                        </span>
                        <span style={{ marginTop: '4px', fontSize: '11px' }}>
                          Preview unavailable
                        </span>
                      </Box>
                    }
                  />

                  {/* Verification Status Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: statusBadge.bgColor,
                      backdropFilter: 'blur(8px)',
                      color: statusBadge.color,
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '10px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }}
                  >
                    {verificationStatus === 'GREEN' && '✓'}
                    {verificationStatus === 'RED' && '✗'}
                    {verificationStatus === 'PENDING' && '⏱'}
                  </Box>
                </Box>
              )}

              {/* File Metadata */}
              {savedImage.fileName && (
                <Box
                  sx={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#FFFFFF05',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '11px', color: '#FFFFFF99', fontWeight: 500 }}>
                      {savedImage.fileName}
                    </Typography>
                    {savedImage.fileSize && (
                      <Typography sx={{ fontSize: '10px', color: '#FFFFFF66' }}>
                        {(savedImage.fileSize / 1024).toFixed(1)} KB
                      </Typography>
                    )}
                  </Box>

                  {savedImage.docType && (
                    <Typography sx={{ fontSize: '10px', color: '#FFFFFF77' }}>
                      Document Type: {savedImage.docType}
                    </Typography>
                  )}

                  {savedImage.uploadedAt && (
                    <Typography sx={{ fontSize: '10px', color: '#FFFFFF66' }}>
                      Uploaded: {new Date(savedImage.uploadedAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Re-upload Hint (if rejected) */}
              {verificationStatus === 'RED' && (
                <Box
                  sx={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    backgroundColor: '#FFA50011',
                    border: '1px dashed #FFA50033',
                    borderRadius: '6px',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '11px', color: '#FFA500', fontWeight: 500 }}>
                    💡 Tip: Click "Remove" above and upload a new, clear photo of your document
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Upload Area - Only show if no saved image OR user removed it */}
          {!savedImage && (
            <Box
              onClick={() => !elementDetail.disabled && fileInputRefs.current[elementDetail.id]?.click()}
              sx={{
                width: '100%',
                height: '350px',
                border: `2px dashed ${errorMessage ? '#ff0000' : '#FFFFFF33'}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '16px',
                backgroundColor: 'transparent',
                color: '#FFFFFF',
                position: 'relative',
                cursor: elementDetail.disabled ? 'not-allowed' : 'pointer',
                opacity: elementDetail.disabled ? 0.6 : 1,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: elementDetail.disabled ? '#FFFFFF33' : '#FFFFFF99',
                },
              }}
            >
              {filePreview && (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    cursor: 'pointer',
                    backgroundColor: 'rgb(255, 255, 255)',
                    borderRadius: '50%',
                    padding: '5px',
                    zIndex: 99999,
                    '&:hover': {
                      backgroundColor: '#FF4444',
                    },
                  }}
                >
                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M1 1L15 15M15 1L1 15'
                      stroke='black'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </Box>
              )}

              <input
                ref={(el) => {
                  if (el) fileInputRefs.current[elementDetail.id] = el;
                }}
                type='file'
                accept={elementDetail.accept || 'image/jpeg,image/png,application/pdf'}
                style={{ display: 'none' }}
                disabled={elementDetail.disabled}
                onChange={handleFileSelect}
              />

              {filePreview ? (
                <>
                  {hasFile && elementDetail.value[0]?.type === 'application/pdf' ? (
                    <embed
                      src={filePreview}
                      type='application/pdf'
                      width='100%'
                      height='100%'
                      style={{
                        borderRadius: '16px',
                        objectFit: 'contain',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                      }}
                    />
                  ) : (
                    <Box
                      component='img'
                      src={filePreview}
                      alt='Selected Preview'
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: '16px',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                      }}
                    />
                  )}
                </>
              ) : (
                <Typography display='flex' flexDirection='column' gap='10px' alignItems='center'>
                  <svg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M18.6367 13.0009L23.7567 7.88086L28.8767 13.0009'
                      stroke='white'
                      strokeWidth='3'
                      strokeMiterlimit='10'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M23.7656 28.3605V8.02051'
                      stroke='white'
                      strokeWidth='3'
                      strokeMiterlimit='10'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M8.00195 24C8.00195 32.84 14.002 40 24.002 40C34.0019 40 40.0019 32.84 40.0019 24'
                      stroke='white'
                      strokeWidth='3'
                      strokeMiterlimit='10'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>Upload Document</span>
                  <Typography variant='subtitle2' className='text-secondary' mb='10px'>
                    Supported formats: {elementDetail.accept ? elementDetail.accept.replace(/[/,\s]/g, ', ') : 'JPG, JPEG, PNG, PDF'}
                  </Typography>
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {renderError(errorMessage)}
      </FormControl >
    );
  };

  const renderLinkField = () => {
    return (
      <div className='custom-html-section' ref={containerRef}>
        <div dangerouslySetInnerHTML={{ __html: elementDetail.title }} />
      </div>
    );
  };

  const renderOTPField = () => {
    return (
      <FormControl style={{ width: '100%', margin: '16px 0', flex: '0 0 100%' }} margin='normal' fullWidth>
        {renderFieldLabel(elementDetail)}

        <Typography variant='body2' sx={{ color: '#a3a3a3', mb: '10px' }}>
          Enter the 6-digit code sent to your phone
        </Typography>

        <Box display='flex' gap='10px'>
          {[...Array(6)].map((_, index) => (
            <TextField
              key={index}
              name={`${elementDetail.id}_otp_${index}`}
              inputProps={{
                maxLength: 1,
                style: { textAlign: 'center', fontSize: '18px', fontWeight: 'bold' },
              }}
              sx={{
                width: '48px',
                '& .MuiOutlinedInput-input': {
                  height: '48px',
                  padding: '0',
                },
              }}
              onChange={(e) => {
                const val = e.target.value;
                if (!/^\d?$/.test(val)) e.target.value = '';
              }}
            />
          ))}
        </Box>

        {renderError(errorMessage)}
      </FormControl>
    );
  };

  // Main render based on field type
  const renderFieldByType = () => {
    switch (elementDetail.type) {
      case FieldType.TEXT:
      case FieldType.EMAIL:
      case FieldType.PASSWORD:
        return renderTextField();

      case FieldType.TEXTAREA:
        return renderTextarea();

      case FieldType.SELECT:
        return renderSelectField();

      case FieldType.CONSENT_SELECT:
        return renderConsentSelectField();

      case FieldType.RADIO:
        return renderRadioField();

      case FieldType.CHECKBOX:
        return renderCheckboxField();

      case FieldType.DATE:
        return renderDateField();

      case FieldType.FILE_UPLOAD:
        return renderFileUploadField();

      case FieldType.PHONE_OTP:
        return (
          <PhoneOTPComponent
            field={elementDetail}
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            validationErrors={validationErrors}
            onChange={onChange}
            showErrorPopup={showErrorPopup}
          />
        );

      case FieldType.OTP:
        return renderOTPField();

      case FieldType.LINK:
        return renderLinkField();

      case FieldType.PHONE:
        return renderPhoneField();

      case FieldType.PLACES_AUTOCOMPLETE:
        let countryCode = selectedValues[13008];
        return (
          <GooglePlacesAutocomplete
            field={elementDetail}
            value={elementDetail.value || ''}
            onChange={onChange}
            onAddressSelected={(placeResult, systemIdentifier) =>
              (onAddressSelected || (() => { }))(placeResult, systemIdentifier)
            }
            errorMessage={errorMessage}
            countryCode={countryCode}
          />
        );

      default:
        return (
          <div style={{ color: 'red', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
            Unknown field type: {elementDetail.type}
          </div>
        );
    }
  };

  return (
    <>
      {renderFieldByType()}

      {/* Consent Popup */}
      {consentField
        // && consentField.consent 
        && (
          <ConsentPopup
            open={showConsentPopup}
            // consent={consentField.consent}
            field={consentField}
            onAccept={handleConsentAccept}
            onDecline={handleConsentDecline}
            isLoading={isLoading}
          />
        )}

      {/* Error/Success Popup */}
      {showPopup && (
        <Successpopup
          Heading={popupData.heading}
          Success={popupData.success}
          onClose={closePopup}
          Message={popupData.message}
        />
      )}
    </>
  );
};

export default FormDynamicElement;
